import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Skeleton,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Header from "../../component/users/Header";
import CompetitionAdvertisementCarousel from "../../component/users/competitions/CompetitionAdvertisementCarousel";
import { fetchCompetitionsList } from "../../action/competitionsListAction";
import { getFashionBanner } from "../../api/fashionPublic";
import { partitionCompetitionsForListing } from "../../utils/competitionListSort";
import { ToastContainer } from "react-toastify";
import { formatCompetitionDateTime } from "../../utils/competitionDateTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
  PAGEANT_EVENTS_DESCRIPTION,
  PAGEANT_EVENTS_SEARCH_PLACEHOLDER,
  PAGEANT_EVENTS_TITLE,
} from "../../utils/homeFlow";

const statusStyles = {
  scheduled: { bg: "rgba(92,110,192,0.95)", label: "Scheduled" },
  running: { bg: "rgba(46,160,80,0.95)", label: "Live" },
  paused: { bg: "rgba(230,140,30,0.95)", label: "Paused" },
  stopped: { bg: "rgba(120,120,130,0.95)", label: "Ended" },
};

function SectionHeading({ children }) {
  return (
    <Typography
      sx={{
        color: "rgba(255,255,255,0.45)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        mb: 1.5,
        mt: 1,
      }}
    >
      {children}
    </Typography>
  );
}

const CompetitionsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem("mynetgoals.com-token");
  const listState = useSelector((state) => state.competitionsList);
  const competitions = Array.isArray(listState?.data?.data)
    ? listState.data.data
    : [];
  const [search, setSearch] = useState("");
  const [fashionBanners, setFashionBanners] = useState([]);

  const { live: liveCompetitions, other: otherCompetitions, all: filtered } =
    useMemo(() => {
      const q = search.trim().toLowerCase();
      const base = !q
        ? competitions
        : competitions.filter((c) => {
            const name = String(c.name || "").toLowerCase();
            const desc = String(c.description || "").toLowerCase();
            const cats = (c.categories || [])
              .map((x) => String(x.category || "").toLowerCase())
              .join(" ");
            return name.includes(q) || desc.includes(q) || cats.includes(q);
          });
      return partitionCompetitionsForListing(base);
    }, [competitions, search]);

  const activeCount = liveCompetitions.length;
  const finalNightLiveCount = filtered.filter(
    (c) => c.final_night_status === "live"
  ).length;

  useEffect(() => {
    if (token) {
      dispatch(fetchCompetitionsList(token));
      getFashionBanner(token)
        .then((response) => setFashionBanners(Array.isArray(response.data?.data) ? response.data.data : []))
        .catch(() => setFashionBanners([]));
    }
  }, [dispatch, token]);

  const renderCompetitionCard = (item, index) => {
    const st = statusStyles[item.status] || statusStyles.scheduled;
    const fnLive = item.final_night_status === "live";
    const fnEnded = item.final_night_status === "ended";
    const isLive = item.status === "running" || fnLive;

    return (
      <Box
        key={item.id}
        onClick={() =>
          fnLive
            ? navigate(`/competitions/${item.id}/final-night`)
            : navigate(`/competitions/${item.id}`)
        }
        sx={{
          position: "relative",
          borderRadius: { xs: 3.5, md: 4 },
          overflow: "hidden",
          cursor: "pointer",
          minHeight: { xs: 380, md: 460 },
          border: isLive
            ? "1px solid rgba(46,160,80,0.35)"
            : "1px solid rgba(255,255,255,0.1)",
          boxShadow: isLive
            ? "0 20px 50px rgba(46,160,80,0.12)"
            : "0 20px 50px rgba(0,0,0,0.35)",
          transition:
            "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
          animation: `fadeUp 0.45s ease ${index * 0.06}s both`,
          "@keyframes fadeUp": {
            from: { opacity: 0, transform: "translateY(14px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          "&:hover": {
            transform: "translateY(-5px)",
            borderColor: "rgba(240,163,175,0.45)",
            boxShadow: "0 28px 60px rgba(0,0,0,0.45)",
          },
          "&:hover .comp-cta": {
            background: "#ef364e",
            color: "#fff",
            borderColor: "#ef364e",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${item.banner_img})`,
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
            transform: "scale(1.04)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `
              linear-gradient(100deg,
                rgba(8,10,16,0.92) 0%,
                rgba(8,10,16,0.72) 32%,
                rgba(8,10,16,0.28) 58%,
                rgba(8,10,16,0.12) 100%),
              linear-gradient(180deg, rgba(8,10,16,0.08), rgba(8,10,16,0.55))
            `,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            minHeight: { xs: 380, md: 460 },
            p: { xs: 2.5, md: 4 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            maxWidth: { md: "52%" },
          }}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              mb={2}
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={fnLive ? "Final Night Live" : st.label}
                sx={{
                  bgcolor: fnLive ? "rgba(217,51,73,0.95)" : st.bg,
                  color: "#fff",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                }}
              />
              {fnEnded && !fnLive ? (
                <Chip
                  size="small"
                  label="Final Night ended"
                  sx={{
                    bgcolor: "rgba(201,166,107,0.85)",
                    color: "#1a1d27",
                    fontWeight: 700,
                  }}
                />
              ) : null}
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
                {formatCompetitionDateTime(item.start_date, "DD MMM")} –{" "}
                {formatCompetitionDateTime(item.end_date, "DD MMM YYYY")}
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: 26, md: 34 },
                fontFamily: "Montserrat, sans-serif",
                letterSpacing: "-0.03em",
                lineHeight: 1.12,
                mb: 1.4,
              }}
            >
              {item.name}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.68)",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.55,
                fontSize: 15,
                maxWidth: 520,
                mb: 2,
              }}
            >
              {item.description}
            </Typography>
            <Stack direction="row" gap={0.7} flexWrap="wrap">
              {(item.categories || []).map((c) => (
                <Chip
                  key={c.category}
                  size="small"
                  label={c.category}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.88)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    fontWeight: 600,
                    fontSize: 12,
                    backdropFilter: "blur(8px)",
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {fnLive ? (
                <Button
                  className="comp-cta"
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/competitions/${item.id}/final-night`);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontFamily: "Montserrat, sans-serif",
                    color: "#fff",
                    border: "1px solid rgba(217,51,73,0.6)",
                    borderRadius: 999,
                    px: 2.4,
                    py: 1,
                    background: "rgba(217,51,73,0.35)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  Watch Final Night
                </Button>
              ) : null}
              <Button
                className="comp-cta"
                endIcon={<ArrowForwardIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/competitions/${item.id}`);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: "Montserrat, sans-serif",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 999,
                  px: 2.4,
                  py: 1,
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.2s ease",
                  pointerEvents: "auto",
                }}
              >
                {fnLive ? "Competition details" : "View competition"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ToastContainer />
      <Header />
      <Box className="userLayout">
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            background: "#242743",
            minHeight: "90vh",
            pb: 8,
            overflowX: "hidden",
            px: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              pt: { xs: 3, md: 4 },
              pb: { xs: 2.5, md: 3 },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-end" }}
              gap={2.5}
              mb={2.5}
            >
              <Box sx={{ maxWidth: 640 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 800,
                    fontSize: { xs: 26, md: 34 },
                    letterSpacing: "-0.03em",
                    lineHeight: 1.1,
                    mb: 1,
                  }}
                >
                  {PAGEANT_EVENTS_TITLE}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    fontSize: { xs: 14, md: 15 },
                    lineHeight: 1.6,
                    maxWidth: 520,
                  }}
                >
                  {PAGEANT_EVENTS_DESCRIPTION}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ alignSelf: { xs: "flex-start", md: "flex-end" }, flexShrink: 0 }}
              >
                <Chip
                  label={`${activeCount} live now`}
                  sx={{
                    height: 34,
                    px: 0.5,
                    bgcolor:
                      activeCount > 0
                        ? "rgba(46,160,80,0.18)"
                        : "rgba(255,255,255,0.06)",
                    color:
                      activeCount > 0 ? "#7ddea0" : "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: 700,
                    fontFamily: "Montserrat, sans-serif",
                  }}
                />
                {finalNightLiveCount > 0 ? (
                  <Chip
                    icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={`${finalNightLiveCount} Final Night live`}
                    sx={{
                      height: 34,
                      bgcolor: "rgba(217,51,73,0.2)",
                      color: "#ff8a9a",
                      border: "1px solid rgba(217,51,73,0.45)",
                      fontWeight: 700,
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>

            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={PAGEANT_EVENTS_SEARCH_PLACEHOLDER}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255,255,255,0.45)" }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      sx={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: 520,
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.05)",
                  borderRadius: 2,
                  "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
                  "&:hover fieldset": {
                    borderColor: "rgba(255,255,255,0.28)",
                  },
                  "&.Mui-focused fieldset": { borderColor: "#ef364e" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.4)",
                  opacity: 1,
                },
              }}
            />

            <CompetitionAdvertisementCarousel token={token} />
          </Box>

          {fashionBanners.map((fashionBanner) => (
          <Box
            key={fashionBanner.id}
            onClick={() => navigate(`/fashion-designers/${fashionBanner.id}`)}
            sx={{
              mb: 2.5,
              position: "relative",
              minHeight: { xs: 380, md: 460 },
              borderRadius: { xs: 3.5, md: 4 },
              overflow: "hidden",
              cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
              transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                borderColor: "rgba(240,163,175,0.45)",
                boxShadow: "0 28px 60px rgba(0,0,0,0.45)",
              },
              "&:hover .comp-cta": {
                background: "#ef364e",
                color: "#fff",
                borderColor: "#ef364e",
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: fashionBanner?.banner_image
                  ? `url(${fashionBanner.banner_image})`
                  : "linear-gradient(120deg, #1c1410, #3a2a24)",
                backgroundSize: "cover",
                backgroundPosition: "center 35%",
                transform: "scale(1.04)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `
                  linear-gradient(100deg,
                    rgba(8,10,16,0.92) 0%,
                    rgba(8,10,16,0.72) 32%,
                    rgba(8,10,16,0.28) 58%,
                    rgba(8,10,16,0.12) 100%),
                  linear-gradient(180deg, rgba(8,10,16,0.08), rgba(8,10,16,0.55))
                `,
              }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                height: "100%",
                minHeight: { xs: 380, md: 460 },
                p: { xs: 2.5, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                maxWidth: { md: "52%" },
              }}
            >
              <Box>
                <Chip
                  size="small"
                  label="Fashion Designer"
                  sx={{
                    mb: 2,
                    bgcolor: "rgba(217,51,73,0.95)",
                    color: "#fff",
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                  }}
                />
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: { xs: 26, md: 34 },
                    fontFamily: "Montserrat, sans-serif",
                    letterSpacing: "-0.03em",
                    lineHeight: 1.12,
                    mb: 1.4,
                  }}
                >
                  {fashionBanner?.title || "The collection"}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.68)",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.55,
                    fontSize: 15,
                    maxWidth: 520,
                  }}
                >
                  {fashionBanner?.description ||
                    "Dress designs, daily voting, and the fashion leaderboard."}
                </Typography>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  className="comp-cta"
                  endIcon={<ArrowForwardIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/fashion-designers/${fashionBanner.id}`);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontFamily: "Montserrat, sans-serif",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: 999,
                    px: 2.4,
                    py: 1,
                    background: "rgba(255,255,255,0.08)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "#ef364e",
                      color: "#fff",
                      borderColor: "#ef364e",
                    },
                  }}
                >
                  View designs
                </Button>
              </Box>
            </Box>
          </Box>
          ))}

          {listState.loading ? (
            <Stack spacing={2.5}>
              {[0, 1].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={440}
                  sx={{ borderRadius: 4, bgcolor: "rgba(255,255,255,0.06)" }}
                />
              ))}
            </Stack>
          ) : listState.error ? (
            <Box
              sx={{
                borderRadius: 4,
                p: { xs: 4, md: 6 },
                textAlign: "center",
                color: "rgba(255,255,255,0.7)",
                bgcolor: "rgba(239,54,78,0.08)",
                border: "1px solid rgba(239,54,78,0.25)",
              }}
            >
              <Typography sx={{ mb: 2 }}>
                Couldn’t load competitions. Please try again.
              </Typography>
              <Button
                variant="contained"
                onClick={() => token && dispatch(fetchCompetitionsList(token))}
                sx={{
                  bgcolor: "#ef364e",
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#d32d43" },
                }}
              >
                Retry
              </Button>
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                borderRadius: 4,
                p: { xs: 4, md: 6 },
                textAlign: "center",
                color: "rgba(255,255,255,0.6)",
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {search.trim()
                ? `No competitions match “${search.trim()}”.`
                : "No competitions available right now."}
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {liveCompetitions.length > 0 ? (
                <Box>
                  <SectionHeading>Live now</SectionHeading>
                  <Stack spacing={2.5}>
                    {liveCompetitions.map((item, index) =>
                      renderCompetitionCard(item, index)
                    )}
                  </Stack>
                </Box>
              ) : null}

              {otherCompetitions.length > 0 ? (
                <Box>
                  {liveCompetitions.length > 0 ? (
                    <SectionHeading>More competitions</SectionHeading>
                  ) : null}
                  <Stack spacing={2.5}>
                    {otherCompetitions.map((item, index) =>
                      renderCompetitionCard(
                        item,
                        liveCompetitions.length + index
                      )
                    )}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
};

export default CompetitionsList;
